import { useState } from "react";
import { RotateCcw, Search } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CAUTARE_TOATE,
  type CautareRaport,
  type RaportSalvat,
} from "@/utils/targetareRaport";
import type { ColoanaTabel, RandRaport } from "@/utils/targetareTabel";
import { MeniuActiuni, type MeniuActiuniProps } from "./MeniuActiuni";

type BaraRaportProps<R extends RandRaport> = {
  coloane: ColoanaTabel<R>[];
  cautare: CautareRaport;
  onCautare: (cautare: CautareRaport) => void;
  rapoarte: RaportSalvat[];
  raportCurent: string;
  onRaport: (nume: string) => void;
  onReset: () => void;
  actiuni: MeniuActiuniProps;
};

export function BaraRaport<R extends RandRaport>({
  coloane,
  cautare,
  onCautare,
  rapoarte,
  raportCurent,
  onRaport,
  onReset,
  actiuni,
}: BaraRaportProps<R>) {
  const [text, setText] = useState(cautare.text);
  const [coloana, setColoana] = useState<string>(cautare.coloana);
  const coloaneText = coloane.filter(
    (c) => c.tip !== "numar" && c.tip !== "bool"
  );
  const etichetaColoana =
    coloana === CAUTARE_TOATE
      ? "Toate coloanele text"
      : (coloane.find((c) => c.id === coloana)?.eticheta ?? coloana);

  const cauta = () => onCautare({ coloana, text: text.trim() });

  return (
    <div className="flex flex-wrap items-center gap-2 rounded-t-lg border bg-muted/40 px-3 py-2">
      <div className="flex items-stretch">
        <Select value={coloana} onValueChange={setColoana}>
          <SelectTrigger
            className="h-9 w-11 rounded-r-none border-r-0 px-2"
            aria-label="Coloana în care se caută"
            title={`Caută în: ${etichetaColoana}`}
          >
            <Search className="h-4 w-4" />
          </SelectTrigger>
          <SelectContent className="max-h-72">
            <SelectItem value={CAUTARE_TOATE}>Toate coloanele text</SelectItem>
            {coloaneText.map((c) => (
              <SelectItem key={c.id} value={c.id}>
                {c.eticheta}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") cauta();
          }}
          placeholder={`Căutare: ${etichetaColoana}`}
          className="h-9 w-64 rounded-none"
          aria-label="Text de căutat"
        />
        <Button
          className="h-9 rounded-l-none"
          variant="secondary"
          onClick={cauta}
        >
          Go
        </Button>
      </div>
      <Select value={raportCurent} onValueChange={onRaport}>
        <SelectTrigger className="h-9 w-[230px]" aria-label="Raport salvat">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {rapoarte.map((r) => (
            <SelectItem key={r.nume} value={r.nume}>
              {r.nume}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <MeniuActiuni {...actiuni} />
      <Button
        variant="outline"
        className="ml-auto h-9"
        onClick={onReset}
        title="Revine la definiția salvată a raportului"
      >
        <RotateCcw className="mr-2 h-4 w-4" />
        Reset
      </Button>
    </div>
  );
}
